// Layer 3 (Transformation): the selected stage's panel on the plant model
// (polish.P11, comp variant A). The panel reads four things that already
// exist — the node summary (the bottleneck read, or the stage's operating
// window read), the subprocess's in / out / recycle, the open circuit's
// stage nodes (`circuitsView` level 2) and the sample points the stage takes
// (`sampleChips`). The two status words are separate reads and are labelled
// that way (adjudication 2): the strip dot is the circuit's SPC read of its
// outcome, the pill here is the bottleneck read on Grinding and the operating
// window read everywhere else.

import type { SignalCell } from '@/versions/v1/schema/spc';
import type { SampleAdherence, SamplePoint, Subprocess } from '@/versions/v1/schema/unit-operation';
import type { ParamChip, StageNode } from './Circuits.transform';
import { SIGNAL_CELL_LABEL, SIGNAL_CELL_TONE, sampleStageId } from './PlantFlowStrip.transform';
import { LATENCY_LABEL, type NodeSummary, type SampleChip } from './Scene.transform';

export type StageReadTone = 'ok' | 'warn' | 'bad';

export type StageRead = {
  kind: 'bottleneck' | 'window';
  label: string;
  tone: StageReadTone;
  text: string;
};

const RAG_WORD: Record<'GREEN' | 'AMBER' | 'RED', { word: string; tone: StageReadTone }> = {
  GREEN: { word: 'Green', tone: 'ok' },
  AMBER: { word: 'Amber', tone: 'warn' },
  RED: { word: 'Red', tone: 'bad' },
};

/** The panel's read line. Null when the stage has neither a bottleneck read
 *  nor an operating-window row (absence is never content). */
export function stageRead(summary: NodeSummary, windowRowCount: number): StageRead | null {
  const [colour, rest] = summary.ragLabel.split(' · ');
  const rag = RAG_WORD[colour as keyof typeof RAG_WORD] ?? RAG_WORD.GREEN;
  if (rest === 'bottleneck read') {
    return { kind: 'bottleneck', label: `Bottleneck read · ${rag.word}`, tone: rag.tone, text: summary.why };
  }
  if (windowRowCount === 0) return null;
  return { kind: 'window', label: `Operating window · ${rag.word}`, tone: rag.tone, text: summary.why };
}

export type StageFact = { label: 'In' | 'Out' | 'Recycle'; value: string; parts: FigureParts };

/** A line split into plain runs and figure runs, so the figures carry the weight. */
export type FigureParts = readonly { text: string; figure: boolean }[];

/** A standalone quantity with its unit ("71.7 stph", "≈1,880 dst"). Digits
 *  inside a tag or name ("P80", "SA01"), bare counts and percentages stay
 *  plain, and so does a basis after "of" ("248% of 75.0 stph fresh feed"). */
const FIGURE = /(?<![A-Za-z0-9.,])(?<!of )≈?\d[\d,]*(?:\.\d+)?\s?(?:kW\/tph|stph|dst|t\/d|t\/h|tph|µm|gpm)(?![A-Za-z])/g;

/** Split a line's runs before its last clause (after the final ` · `, else
 *  before its last word), so the UI can hold that clause to the off pill that
 *  follows it: the line wraps as a unit, `Design 12 DRIFT` stays together and
 *  the pill never lands on a line of its own (polish.P11 r2). */
export function splitLineTail(parts: FigureParts): { lead: FigureParts; tail: FigureParts } {
  const last = parts[parts.length - 1];
  if (!last) return { lead: [], tail: [] };
  const text = last.text.trimEnd();
  const dot = text.lastIndexOf(' · ');
  const cut = dot >= 0 ? dot + 3 : text.lastIndexOf(' ') + 1;
  if (cut <= 0 || last.figure) return { lead: parts.slice(0, -1), tail: [last] };
  return {
    lead: [...parts.slice(0, -1), { text: last.text.slice(0, cut), figure: false }],
    tail: [{ text: last.text.slice(cut), figure: false }],
  };
}

export function figureParts(text: string): FigureParts {
  const parts: { text: string; figure: boolean }[] = [];
  let cursor = 0;
  for (const match of text.matchAll(FIGURE)) {
    if (match.index > cursor) parts.push({ text: text.slice(cursor, match.index), figure: false });
    parts.push({ text: match[0], figure: true });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), figure: false });
  return parts;
}

export function stageFacts(subprocess: Subprocess | undefined): StageFact[] {
  if (!subprocess) return [];
  return [
    { label: 'In' as const, value: subprocess.inputs },
    { label: 'Out' as const, value: subprocess.outputs },
    { label: 'Recycle' as const, value: subprocess.recirculatingLoad },
  ]
    .filter((row) => row.value)
    .map((row) => ({ ...row, parts: figureParts(row.value) }));
}

export type StageParamLine = {
  id: string;
  text: string;
  /** The line split around the parameter's value, which carries the weight. */
  parts: FigureParts;
  /** The parameter's own off word, when the row's header word does not already say it. */
  pill: { word: string; tone: 'warn' | 'bad' } | null;
};

export type StageRowStatus =
  | { kind: 'pill'; word: string; tone: 'warn' | 'bad' }
  /** A multi-line row whose off words sit on the off lines themselves. */
  | { kind: 'on-lines' }
  | { kind: 'quiet' }
  | { kind: 'not-computed' };

export type StageRow = {
  id: string;
  label: string;
  lines: readonly StageParamLine[];
  inBandLine: string | null;
  notComputedLine: string | null;
  status: StageRowStatus;
};

function isOff(cell: ParamChip['cell']): cell is SignalCell {
  return cell !== 'quiet' && cell !== 'not-computed' && cell !== 'no-series';
}

function paramParts(p: ParamChip): FigureParts {
  const basis = p.basisLabel ? ` · ${p.basisLabel} ${p.basisValue?.toLocaleString() ?? ''}` : '';
  // "Zn float pH 10.2", not "Zn float pH 10.2 pH": a label that already names the unit keeps it once.
  const unit = p.unit && !p.label.endsWith(` ${p.unit}`) ? ` ${p.unit}` : '';
  return [
    { text: `${p.label} `, figure: false },
    { text: `${p.value}${unit}`, figure: true },
    ...(basis ? [{ text: basis, figure: false }] : []),
  ];
}

/** The open circuit's stages as panel rows: the SPC chips, the in-band count
 *  (rule of three) and the stage's off word. The word is said once, where it
 *  applies: a single-line stage carries it on the row, a multi-line stage on
 *  each line that is off (polish.P11 r1 — Pb Cleaners reads DRIFT on its Ag
 *  line, not beside its in-band count). */
export function stageRows(stages: readonly StageNode[]): StageRow[] {
  return stages.map((node) => {
    const multiLine = node.params.length > 1;
    const worst = node.worstCell;
    const status: StageRowStatus =
      worst === 'not-computed'
        ? { kind: 'not-computed' }
        : worst === 'quiet'
          ? { kind: 'quiet' }
          : multiLine && node.params.some((p) => isOff(p.cell))
            ? { kind: 'on-lines' }
            : { kind: 'pill', word: SIGNAL_CELL_LABEL[worst] ?? worst, tone: SIGNAL_CELL_TONE[worst] ?? 'warn' };
    return {
      id: node.id,
      label: node.label,
      lines: node.params.map((p) => {
        const parts = paramParts(p);
        return {
          id: p.readingId,
          text: parts.map((part) => part.text).join(''),
          parts,
          pill:
            isOff(p.cell) && multiLine && SIGNAL_CELL_LABEL[p.cell]
              ? { word: SIGNAL_CELL_LABEL[p.cell]!, tone: SIGNAL_CELL_TONE[p.cell] ?? 'warn' }
              : null,
        };
      }),
      inBandLine: node.quietCount > 0 ? `${node.quietCount} in band` : null,
      notComputedLine:
        node.notComputedCount > 0 && worst !== 'not-computed' ? `${node.notComputedCount} not computed` : null,
      status,
    };
  });
}

/** SA01 confirms the hardness read, so it keeps its own row even when routine. */
const HARDNESS_CONFIRM_SAMPLE = 'SA01';

export type StageSampleRow = {
  id: string;
  stream: string;
  spec: string;
  latency: string;
  adherence: SampleAdherence;
  tone: 'neutral' | 'warn' | 'bad' | null;
};

export type StageSamples = {
  rows: readonly StageSampleRow[];
  /** Routine (REG) samples folded to one line. */
  routine: readonly { id: string; stream: string; spec: string }[];
  count: number;
};

function adherenceTone(adherence: SampleAdherence): StageSampleRow['tone'] {
  if (adherence === 'REG') return null;
  if (adherence === 'MISS') return 'bad';
  if (adherence === 'LATE') return 'warn';
  return 'neutral';
}

/** The sample points the selected stage takes, off-routine ones first-class. */
export function stageSamples(stageId: string, samples: readonly SampleChip[]): StageSamples {
  const mine = samples.filter((sample) => sampleStageId(sample) === stageId);
  const own = (sample: SamplePoint) => sample.adherence !== 'REG' || sample.id === HARDNESS_CONFIRM_SAMPLE;
  return {
    rows: mine.filter(own).map((sample) => ({
      id: sample.id,
      stream: sample.stream,
      spec: sample.spec,
      latency: LATENCY_LABEL[sample.latencyClass],
      adherence: sample.adherence,
      tone: adherenceTone(sample.adherence),
    })),
    routine: mine
      .filter((sample) => !own(sample))
      .map((sample) => ({ id: sample.id, stream: sample.stream, spec: sample.spec })),
    count: mine.length,
  };
}
